import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { UserAPI } from "../../../../shared/types/api";

/**
 * Pending invites targeted at the current user — drives the dashboard
 * banner. Server matches by inviteeUserId OR email so a brand-new user who
 * was invited by email also sees their invite.
 */
export function useIncomingInvites(enabled = true) {
  return useQuery({
    queryKey: ["incomingInvites"],
    queryFn: async (): Promise<UserAPI.ListIncomingInvitesResponse> => {
      return api.get(`/user/invites/incoming`);
    },
    enabled,
  });
}

/**
 * Public preview — no auth required. Used on the /invite/:code landing page
 * to show "Sascha invited you to Tuesday dinner in Chelsea" before sign-up.
 */
export function useInvitePreview(code: string | null) {
  return useQuery({
    queryKey: ["invitePreview", code],
    queryFn: async (): Promise<UserAPI.GetBookingInvitePreviewResponse> => {
      return api.get(`/invites/${code}`);
    },
    enabled: !!code,
    retry: false,
  });
}

export function useListInvitesForBooking(bookingId: string | null) {
  return useQuery({
    queryKey: ["bookingInvites", bookingId],
    queryFn: async (): Promise<UserAPI.ListBookingInvitesResponse> => {
      return api.get(`/user/invites/by-booking/${bookingId}`);
    },
    enabled: !!bookingId,
  });
}

export function useCreateBookingInvite(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: UserAPI.CreateBookingInviteRequest,
    ): Promise<UserAPI.CreateBookingInviteResponse> => {
      return api.post(`/user/invites/by-booking/${bookingId}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bookingInvites", bookingId] });
    },
  });
}

export function useCancelBookingInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      inviteId: string,
    ): Promise<UserAPI.CancelBookingInviteResponse> => {
      return api.post(`/user/invites/${inviteId}/cancel`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bookingInvites"] });
    },
  });
}

export function useAcceptBookingInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      code: string,
    ): Promise<UserAPI.AcceptBookingInviteResponse> => {
      return api.post(`/user/invites/${code}/accept`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reservations"] });
    },
  });
}

export function useFinalizeBookingInvite() {
  return useMutation({
    mutationFn: async (inviteId: string) => {
      return api.post(`/user/invites/${inviteId}/finalize`);
    },
  });
}
